import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  Video,
  User,
  Briefcase,
  Loader,
  Star,
  ChevronLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  Eye,
  MapPin,
  DollarSign,
  FileText,
} from "lucide-react";
import { mentorshipApi, showError, showSuccess } from "../lib";
import { useAuth } from "../context/AuthContext";

interface Session {
  _id: string;
  mentor: {
    _id: string;
    user?: {
      name: string;
      firstName?: string;
      lastName?: string;
      avatar?: string;
    };
    specialty?: string;
    company?: string;
    expertise?: string[];
    rating?: number;
    reviewsCount?: number;
    avatar?: string;
  };
  student: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  scheduledAt: string;
  duration: number;
  status: "pending" | "confirmed" | "completed" | "cancelled" | "no-show";
  price: number;
  topic: string;
  notes?: string;
  meetingLink?: string;
  createdAt: string;
}

const MentorshipSessionDetailPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (sessionId) fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      const response = await mentorshipApi.getSessionDetails(sessionId!);
      if (response.success) {
        setSession(response.data);
      } else {
        showError("Session not found");
        navigate("/dashboard/student/mentorship-sessions");
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to load session");
      navigate("/dashboard/student/mentorship-sessions");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSession = async () => {
    if (!session) return;
    if (!confirm(`Cancel this session?\n\nTopic: ${session.topic}`)) return;
    try {
      setActionLoading(true);
      const res = await mentorshipApi.cancelSession(session._id);
      if (res.success) {
        showSuccess("Session cancelled");
        fetchSession();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to cancel session");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveSession = async () => {
    if (!session) return;
    try {
      setActionLoading(true);
      const res = await mentorshipApi.confirmSession(session._id);
      if (res.success) {
        showSuccess(
          res.data?.meetingLink
            ? `Approved! Link: ${res.data.meetingLink}`
            : "Session approved!",
        );
        fetchSession();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to approve session");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSession = async () => {
    if (!session) return;
    if (
      !confirm(
        `Delete this session? This cannot be undone.\n\nTopic: ${session.topic}`,
      )
    )
      return;
    try {
      setActionLoading(true);
      const res = await mentorshipApi.deleteSession(session._id);
      if (res.success) {
        showSuccess("Session deleted");
        navigate(
          user?.role === "mentor"
            ? "/dashboard/mentor/sessions"
            : user?.role === "admin"
              ? "/dashboard/admin/sessions"
              : "/dashboard/student/mentorship-sessions",
        );
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to delete session");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!session) return;
    try {
      setActionLoading(true);
      const res = await mentorshipApi.completeSession(session._id);
      if (res.success) {
        showSuccess("Marked as completed");
        fetchSession();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to complete session");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<
      string,
      { bg: string; text: string; dot: string; label: string }
    > = {
      pending: {
        bg: "bg-amber-50",
        text: "text-amber-700",
        dot: "bg-amber-400",
        label: "Pending",
      },
      confirmed: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        dot: "bg-emerald-400",
        label: "Confirmed",
      },
      completed: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        dot: "bg-blue-400",
        label: "Completed",
      },
      cancelled: {
        bg: "bg-red-50",
        text: "text-red-700",
        dot: "bg-red-400",
        label: "Cancelled",
      },
      "no-show": {
        bg: "bg-gray-100",
        text: "text-gray-600",
        dot: "bg-gray-400",
        label: "No-Show",
      },
    };
    return configs[status] || configs.pending;
  };

  const fmtXAF = (amount: number) =>
    new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const getDisplayName = (person: any) => {
    if (!person) return "Unknown";
    if (person.name) return person.name;
    return (
      ((person.firstName || "") + " " + (person.lastName || "")).trim() ||
      "Unknown"
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
          <p className="text-gray-400 text-xs">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm mb-3">Session not found</p>
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(session.status);
  const dateInfo = formatDateTime(session.scheduledAt);
  const isStudent = user?.role === "student";
  const isMentor = user?.role === "mentor";
  const isAdmin = user?.role === "admin";

  const canCancel =
    (isStudent || isMentor || isAdmin) &&
    ["pending", "confirmed"].includes(session.status);
  const canApprove = (isMentor || isAdmin) && session.status === "pending";
  const canComplete =
    (isStudent || isMentor || isAdmin) && session.status === "confirmed";
  const canDelete = isAdmin || (isMentor && session.mentor._id === user?._id);
  const canReview = isStudent && session.status === "completed";

  return (
    <main className="min-h-screen pt-10 pb-10 bg-gray-50">
      <div className="max-w-3xl mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-gray-400 hover:text-primary text-xs font-medium mb-3 transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back to sessions
          </button>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-base font-bold text-gray-900 leading-tight">
                Session Details
              </h1>
              <p className="text-[11px] text-gray-400 mt-0.5">
                View and manage mentorship session
              </p>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusConfig.bg} ${statusConfig.text} whitespace-nowrap`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}
              />
              {statusConfig.label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* ── Main Card ── */}
          <div className="lg:col-span-2 space-y-3">
            {/* Topic */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Video className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-gray-900 truncate">
                    {session.topic || "Mentorship Session"}
                  </h2>
                  <p className="text-[11px] text-gray-400">Topic discussion</p>
                </div>
              </div>

              {/* Meta grid — 2 cols on all sizes */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    icon: Calendar,
                    color: "bg-blue-50 text-blue-500",
                    label: "Date",
                    value: dateInfo.date,
                  },
                  {
                    icon: Clock,
                    color: "bg-green-50 text-green-500",
                    label: "Time",
                    value: dateInfo.time,
                  },
                  {
                    icon: DollarSign,
                    color: "bg-violet-50 text-violet-500",
                    label: "Price",
                    value: fmtXAF(session.price),
                  },
                  {
                    icon: Clock,
                    color: "bg-orange-50 text-orange-500",
                    label: "Duration",
                    value: `${session.duration} min`,
                  },
                ].map(({ icon: Icon, color, label, value }) => (
                  <div
                    key={label}
                    className="bg-gray-50 rounded-lg p-3 flex items-center gap-2.5"
                  >
                    <div
                      className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${color}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] text-gray-400 uppercase tracking-wide font-semibold">
                        {label}
                      </p>
                      <p className="text-xs font-semibold text-gray-800 truncate leading-tight mt-0.5">
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            {session.notes && (
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-start gap-2.5">
                  <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] text-blue-600 font-semibold uppercase tracking-wide mb-1">
                      Notes
                    </p>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {session.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Meeting Link */}
            {session.meetingLink && (
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-start gap-2.5">
                  <Video className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold mb-1.5">
                      Meeting Link
                    </p>
                    <a
                      href={session.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 text-xs font-semibold transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Join meeting
                    </a>
                    <p className="text-[10px] text-gray-400 mt-1 break-all leading-relaxed">
                      {session.meetingLink}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            {(canCancel ||
              canApprove ||
              canComplete ||
              canReview ||
              canDelete) && (
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold mb-3">
                  Actions
                </p>
                <div className="flex flex-wrap gap-2">
                  {canApprove && (
                    <button
                      onClick={handleApproveSession}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      {actionLoading ? "Processing…" : "Approve"}
                    </button>
                  )}
                  {canComplete && (
                    <button
                      onClick={handleCompleteSession}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      {actionLoading ? "Processing…" : "Mark Complete"}
                    </button>
                  )}
                  {canCancel && (
                    <button
                      onClick={handleCancelSession}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      {actionLoading ? "Processing…" : "Cancel"}
                    </button>
                  )}
                  {canReview && (
                    <Link
                      to={`/mentorship/sessions/${session._id}/review`}
                      className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold rounded-lg transition-colors"
                    >
                      <Star className="w-3.5 h-3.5" />
                      Leave Review
                    </Link>
                  )}
                  {canDelete && (
                    <button
                      onClick={handleDeleteSession}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-500 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {actionLoading
                        ? "Processing…"
                        : isAdmin
                          ? "Delete Session"
                          : "Delete"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar ── */}
          <div className="space-y-3">
            {/* Student */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold mb-3">
                Student
              </p>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {getDisplayName(session.student)}
                  </p>
                  {session.student?.email && (
                    <p className="text-[10px] text-gray-400 truncate mt-0.5">
                      {session.student.email}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Mentor */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold mb-3">
                Mentor
              </p>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <p className="text-xs font-semibold text-gray-900 truncate">
                  {getDisplayName(session.mentor?.user)}
                </p>
              </div>
              {session.mentor && (
                <div className="space-y-1.5">
                  {session.mentor.specialty && (
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                      <Briefcase className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate">
                        {session.mentor.specialty}
                      </span>
                    </div>
                  )}
                  {session.mentor.company && (
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                      <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{session.mentor.company}</span>
                    </div>
                  )}
                  {session.mentor.rating != null && (
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <Star className="w-3 h-3 text-amber-400 flex-shrink-0" />
                      <span className="text-amber-600 font-semibold">
                        {session.mentor.rating}/5
                      </span>
                      <span className="text-gray-400">
                        ({session.mentor.reviewsCount || 0} reviews)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Session Info */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold mb-3">
                Session Info
              </p>
              <div className="space-y-2">
                {[
                  {
                    label: "Status",
                    value: statusConfig.label,
                    className: `font-semibold ${statusConfig.text}`,
                  },
                  {
                    label: "Created",
                    value: new Date(session.createdAt).toLocaleDateString(),
                    className: "text-gray-700",
                  },
                  {
                    label: "ID",
                    value: session._id.slice(0, 10) + "…",
                    className: "font-mono text-gray-400 text-[10px]",
                  },
                ].map(({ label, value, className }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-[11px] text-gray-400">{label}</span>
                    <span className={`text-[11px] ${className}`}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default MentorshipSessionDetailPage;
